import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from './ui/dialog';
import { 
  Share,
  Plus,
  Smartphone,
  CheckCircle,
  X,
  ChevronRight,
  ChevronLeft,
  Download,
  ArrowDown
} from 'lucide-react';

const STORAGE_KEY = 'gym_notebook_ios_install_dismissed';
const INSTALLED_KEY = 'gym_notebook_ios_installed'; // Permanent flag for installed users
const DAYS_BETWEEN_PROMPTS = 7; // Show again after 7 days if dismissed

// Custom iOS Share icon (Safari's share button)
const IOSShareIcon = () => (
  <svg 
    className="w-6 h-6" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M12 3v12" />
    <path d="M8 7l4-4 4 4" />
    <path d="M20 21H4a2 2 0 01-2-2v-9a2 2 0 012-2h4" />
    <path d="M16 10h4a2 2 0 012 2v7a2 2 0 01-2 2" />
  </svg>
);

// Installation steps
const INSTALL_STEPS = [
  {
    id: 'share',
    title: 'Tap the Share Button',
    description: 'Look for the share icon at the bottom of Safari (or top on iPad)',
    icon: IOSShareIcon,
    illustration: (
      <div className="flex justify-center items-center py-6">
        <div className="relative">
          {/* Phone mockup */}
          <div className="w-48 h-80 bg-slate-800 rounded-3xl border-4 border-slate-600 overflow-hidden">
            {/* Screen content */}
            <div className="h-full bg-gradient-to-b from-slate-900 to-slate-800 p-3">
              <div className="w-full h-6 bg-slate-700/50 rounded mb-2"></div>
              <div className="w-3/4 h-4 bg-slate-700/50 rounded mb-4"></div>
              <div className="flex gap-2 mb-4">
                <div className="flex-1 h-20 bg-slate-700/30 rounded-lg"></div>
                <div className="flex-1 h-20 bg-slate-700/30 rounded-lg"></div>
              </div>
              <div className="w-full h-4 bg-slate-700/50 rounded mb-2"></div>
              <div className="w-2/3 h-4 bg-slate-700/50 rounded"></div>
            </div>
          </div>
          {/* Safari toolbar */}
          <div className="absolute bottom-0 left-0 right-0 h-14 bg-slate-700 border-t border-slate-600 flex items-center justify-around px-4 rounded-b-2xl">
            <div className="w-6 h-6 bg-slate-600 rounded"></div>
            <div className="w-6 h-6 bg-slate-600 rounded"></div>
            {/* Share button highlighted */}
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-blue-500/50">
              <IOSShareIcon />
            </div>
            <div className="w-6 h-6 bg-slate-600 rounded"></div>
            <div className="w-6 h-6 bg-slate-600 rounded"></div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'add-to-home',
    title: 'Select "Add to Home Screen"',
    description: 'Scroll down in the share menu and tap "Add to Home Screen"',
    icon: Plus,
    illustration: (
      <div className="flex justify-center items-center py-6">
        <div className="w-64 bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
          {/* Share sheet mockup */}
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center">
                <span className="text-white text-xl">🏋️</span>
              </div>
              <div>
                <p className="text-white font-medium text-sm">Fitness Notebook</p>
                <p className="text-slate-400 text-xs">nutristeps.me</p>
              </div>
            </div>
          </div>
          {/* Action buttons row */}
          <div className="p-4 border-b border-slate-700">
            <div className="flex gap-4 overflow-hidden">
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-12 bg-slate-700 rounded-xl"></div>
                <span className="text-xs text-slate-400">Copy</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-12 bg-slate-700 rounded-xl"></div>
                <span className="text-xs text-slate-400">Mail</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-12 bg-slate-700 rounded-xl"></div>
                <span className="text-xs text-slate-400">Message</span>
              </div>
            </div>
          </div>
          {/* Action list */}
          <div className="py-2">
            <div className="px-4 py-3 flex items-center gap-3 hover:bg-slate-700/50">
              <div className="w-8 h-8 bg-slate-700 rounded-lg flex items-center justify-center">
                <span className="text-sm">📖</span>
              </div>
              <span className="text-white text-sm">Add to Reading List</span>
            </div>
            {/* Highlighted option */}
            <div className="px-4 py-3 flex items-center gap-3 bg-blue-500/20 border-l-4 border-blue-500">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <Plus className="w-5 h-5 text-white" />
              </div>
              <span className="text-white text-sm font-medium">Add to Home Screen</span>
              <ChevronRight className="w-4 h-4 text-blue-400 ml-auto" />
            </div>
            <div className="px-4 py-3 flex items-center gap-3 hover:bg-slate-700/50">
              <div className="w-8 h-8 bg-slate-700 rounded-lg flex items-center justify-center">
                <span className="text-sm">📌</span>
              </div>
              <span className="text-white text-sm">Add Bookmark</span>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'confirm',
    title: 'Tap "Add" to Install',
    description: 'Confirm the name and tap "Add" in the top right corner',
    icon: CheckCircle,
    illustration: (
      <div className="flex justify-center items-center py-6">
        <div className="w-64 bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
            <button className="text-blue-400 text-sm">Cancel</button>
            <span className="text-white font-medium">Add to Home Screen</span>
            <button className="text-blue-400 text-sm font-semibold bg-blue-500/20 px-3 py-1 rounded-lg animate-pulse">
              Add
            </button>
          </div>
          {/* Content */}
          <div className="p-6 flex flex-col items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <span className="text-3xl">🏋️</span>
            </div>
            <div className="w-full bg-slate-900 rounded-lg p-3 border border-slate-700">
              <span className="text-white text-center block">Fitness Notebook</span>
            </div>
            <p className="text-slate-400 text-xs mt-3 text-center">
              An icon will be added to your home screen so you can quickly access this website.
            </p>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'done',
    title: 'You\'re All Set!',
    description: 'Find the Fitness Notebook icon on your home screen and enjoy the full app experience!',
    icon: Smartphone,
    illustration: (
      <div className="flex justify-center items-center py-6">
        <div className="relative">
          {/* Home screen mockup */}
          <div className="w-64 h-48 bg-gradient-to-br from-purple-900 via-slate-900 to-blue-900 rounded-2xl p-4 border border-slate-700">
            <div className="grid grid-cols-4 gap-3">
              {/* App icons */}
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-12 bg-green-500 rounded-xl"></div>
                <span className="text-white text-xs">Phone</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-12 bg-blue-500 rounded-xl"></div>
                <span className="text-white text-xs">Safari</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl"></div>
                <span className="text-white text-xs">Photos</span>
              </div>
              {/* Our app highlighted */}
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/30 ring-2 ring-orange-400 ring-offset-2 ring-offset-slate-900 animate-pulse">
                  <span className="text-lg">🏋️</span>
                </div>
                <span className="text-orange-400 text-xs font-medium">Fitness</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-12 bg-slate-700 rounded-xl"></div>
                <span className="text-white text-xs">Music</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-12 bg-slate-700 rounded-xl"></div>
                <span className="text-white text-xs">Notes</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-12 bg-slate-700 rounded-xl"></div>
                <span className="text-white text-xs">Clock</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-12 bg-slate-700 rounded-xl"></div>
                <span className="text-white text-xs">Settings</span>
              </div>
            </div>
          </div>
          {/* Arrow pointing to our app */}
          <div className="absolute -top-4 right-8 text-orange-400">
            <ArrowDown className="w-6 h-6 animate-bounce" />
          </div>
        </div>
      </div>
    )
  }
];

export default function IOSInstallGuide({ forceShow = false, onClose }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  
  // Detect iOS
  const isIOS = typeof navigator !== 'undefined' && 
    /iPad|iPhone|iPod/.test(navigator.userAgent) && 
    !window.MSStream;
  
  // Detect if already installed as PWA (standalone mode)
  const isStandalone = typeof window !== 'undefined' && 
    (window.navigator.standalone === true || 
     window.matchMedia('(display-mode: standalone)').matches);

  useEffect(() => {
    if (forceShow) {
      setIsOpen(true);
      setCurrentStep(0);
      return;
    }

    // Only show for iOS Safari users who haven't installed yet
    if (!isIOS || isStandalone) {
      // If running as standalone (installed), permanently mark as installed
      if (isStandalone) {
        localStorage.setItem(INSTALLED_KEY, 'true');
      }
      return;
    }
    
    // Check if user has already installed the app (permanent flag)
    const hasInstalled = localStorage.getItem(INSTALLED_KEY) === 'true';
    if (hasInstalled) {
      return; // Never show again for users who have installed
    }
    
    // Check if user has dismissed recently
    const dismissedData = localStorage.getItem(STORAGE_KEY);
    if (dismissedData) {
      try {
        const { dismissedAt } = JSON.parse(dismissedData);
        const daysSinceDismissed = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < DAYS_BETWEEN_PROMPTS) {
          return; // Don't show if dismissed within the last N days
        }
      } catch (e) {
        // Invalid data, continue showing
      }
    }
    
    // Show the guide after a short delay (let user see the app first)
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 3000); // 3 second delay
    
    return () => clearTimeout(timer);
  }, [forceShow, isIOS, isStandalone]);

  const handleClose = () => {
    // Save dismissal timestamp
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      dismissedAt: Date.now()
    }));
    setIsOpen(false);
    setCurrentStep(0);
    onClose?.();
  };

  const handleNeverShow = () => {
    // Set a far future date to effectively never show again
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      dismissedAt: Date.now() + (365 * 24 * 60 * 60 * 1000) // 1 year from now
    }));
    setIsOpen(false);
    setCurrentStep(0);
    onClose?.();
  };

  const nextStep = () => {
    if (currentStep < INSTALL_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const step = INSTALL_STEPS[currentStep];
  const IconComponent = step?.icon || Download;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent 
        className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-slate-700 max-w-[90vw] sm:max-w-sm p-0 overflow-hidden max-h-[85vh] overflow-y-auto"
        data-testid="ios-install-guide-modal"
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-400 p-4 sm:p-5 pb-3 sm:pb-4">
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 sm:top-3 sm:right-3 text-white/80 hover:text-white transition-colors"
            data-testid="ios-install-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <Download className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-white text-base sm:text-lg font-bold">
                Install the App
              </DialogTitle>
              <DialogDescription className="text-white/80 text-xs sm:text-sm">
                Add to your home screen
              </DialogDescription>
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="px-3 sm:px-5 pt-3 sm:pt-4">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-2 sm:mb-4">
            <span className="text-xs text-slate-400">
              Step {currentStep + 1} of {INSTALL_STEPS.length}
            </span>
          </div>

          {/* Step title */}
          <div className="text-center mb-2">
            <div className="inline-flex items-center gap-2 bg-slate-800 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-slate-700">
              <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
              <h3 className="text-white font-semibold text-sm sm:text-base">
                {step.title}
              </h3>
            </div>
          </div>

          {/* Step description */}
          <p className="text-slate-400 text-xs sm:text-sm text-center mb-2">
            {step.description}
          </p>

          {/* Illustration - scaled down for mobile */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 mb-3 sm:mb-4 transform scale-[0.85] sm:scale-100 origin-top">
            {step.illustration}
          </div>
        </div>

        {/* Navigation */}
        <div className="px-3 sm:px-5 pb-4 sm:pb-5">
          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-3 sm:mb-4">
            {INSTALL_STEPS.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentStep
                    ? 'w-6 bg-cyan-400'
                    : 'w-2 bg-slate-600 hover:bg-slate-500'
                }`}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={prevStep}
              onTouchEnd={(e) => { e.preventDefault(); prevStep(); }}
              disabled={currentStep === 0}
              className="text-slate-400 hover:text-white disabled:opacity-30 text-sm touch-manipulation"
              size="sm"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>

            {currentStep === INSTALL_STEPS.length - 1 ? (
              <Button
                onClick={handleClose}
                onTouchEnd={(e) => { e.preventDefault(); handleClose(); }}
                className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-4 sm:px-5 text-sm touch-manipulation"
                size="sm"
                data-testid="ios-install-done-btn"
              >
                <CheckCircle className="h-4 w-4 mr-1 sm:mr-2" />
                Got it!
              </Button>
            ) : (
              <Button
                onClick={nextStep}
                onTouchEnd={(e) => { e.preventDefault(); nextStep(); }}
                className="bg-cyan-500 hover:bg-cyan-600 text-white text-sm touch-manipulation"
                size="sm"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>

          {/* Don't show again link */}
          {currentStep === 0 && (
            <div className="mt-3 sm:mt-4 text-center">
              <button
                onClick={handleNeverShow}
                onTouchEnd={(e) => { e.preventDefault(); handleNeverShow(); }}
                className="text-xs text-slate-500 hover:text-slate-400 transition-colors touch-manipulation"
              >
                Don't show this again
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Export a hook for manual triggering
export function useIOSInstallGuide() {
  const [showGuide, setShowGuide] = useState(false);
  
  const isIOS = typeof navigator !== 'undefined' && 
    /iPad|iPhone|iPod/.test(navigator.userAgent) && 
    !window.MSStream;
  
  const isStandalone = typeof window !== 'undefined' && 
    (window.navigator.standalone === true || 
     window.matchMedia('(display-mode: standalone)').matches);
  
  return {
    showGuide,
    openGuide: () => setShowGuide(true),
    closeGuide: () => setShowGuide(false),
    isIOS,
    isInstalled: isStandalone,
    canShow: isIOS && !isStandalone
  };
}
